import sequenceService from "../SequenceService";
import listService from "../ListService";
import mobileConstants from '../../mobile/commons/MobileFormsConstants';
import utilMobile from '../../mobile/commons/MobileGeneralFunctions';
import Immutable from 'immutable';
import util from '../util/Util';
import mobileListService from "./MobileListService";
import constants from 'Constants';

import { ensureArray } from '../../components/Common/Utils';
import {
    ensurePxUnit,
    processControlModel,
    convertSavedPropertiesToAttributes,
    normalizeSectionAttributesForWizard
} from '../../components/MobileWizard/utils/mobileUtils';

class MobileLoadService {

    initializeMobileLayouts(form1Data, form2Data) {

        if (form1Data?.mobileForm?.length) {
            this._initializeFromMobileForm(form1Data.mobileForm[0]);
        }
        if (form2Data?.mobileForm?.length) {
            this._initializeTwoColumnsConfiguration(form2Data.mobileForm[0]);
        }
    }

    initializeMobileStylesAndSettings(mobileSettings) {
        listService.iniMobileGlobalSettings({});
        const settings = mobileSettings?.Settings;
        if (settings) {
            if (settings.MobileGlobalStyles?.ControlStyle) {
                const stylesFromXml = ensureArray(settings.MobileGlobalStyles.ControlStyle);

                const stylesObjectForMap = stylesFromXml.reduce((acc, style) => {
                    if (style && style.factoryKey) {
                        acc[style.factoryKey] = style;
                    }
                    return acc;
                }, {});

                const loadedStylesMap = Immutable.Map(stylesObjectForMap);

                listService.iniMobileStyle(loadedStylesMap);
            }

            if (settings.fontFamily !== undefined) {
                listService.addMobileSetting('fontFamily', settings.fontFamily);
            }
            
            const marginStyle = listService.getMobileStyleByKey("control_margin");
            listService.addMobileSetting('marginSpacing', marginStyle.marginSize);
            listService.addMobileSetting('defaultMargin', marginStyle.defaultMargin);
        }
    }

    _initializeTwoColumnsConfiguration(mobileForm) {

        const ensureArray = (item) => {
            if (!item) return [];
            return Array.isArray(item) ? item : [item];
        };

        const pages = ensureArray(mobileForm.Form?.Pages?.Page);
        const columns = ensureArray(mobileForm.Form?.Columns?.Column);
        const sections = ensureArray(mobileForm.Form?.Sections?.Section);

        pages.forEach(page => {
            columns.filter(column => column._pageId === page._id).forEach(column => {
                let newColumn = {};
                newColumn.id = column._id;
                newColumn.name = column._name;
                newColumn.pageId = listService.cleanId(column._pageId);
                newColumn.order = parseInt(column._order);
                newColumn.sections = Immutable.Map();
                sections.filter(section => section._columnId === column._id).forEach(section => {
                    newColumn.sections = newColumn.sections.set(section._id, { id: section._id, order: parseInt(section._order) });
                });
                listService.addMobileColumn(newColumn.id, newColumn);
            });
        });
    }

    _initializeFromMobileForm(mobileForm) {
        //Loading pages
        const pagesId = this._loadFormPages(mobileForm);

        //including new pages added on form desktop
        this._includeNewFormPages(pagesId);

        //loading columns:
        this._loadFormColumns(mobileForm);

        //loading sections
        this._loadFormSections(mobileForm);

        //loading Objects
        const objectsId = this._loadFormObjects(mobileForm);

        //including new objects added on form desktop
        this.includeNewObjects(objectsId);

        //Link the sections to their columns
        this._buildColumnSectionLinks();

        // initialize the limited designer
        this.loadMobileFormDesign(mobileForm);

    }

    _loadFormPages(mobileForm) {
        const savedPages = ensureArray(mobileForm.Form?.Pages?.Page);
        const loadedPageIds = [];

        listService._mobilePages = Immutable.Map();

        savedPages.forEach(pageData => {
            const pageId = listService.getCleanedId(pageData._id);

            // Check if the corresponding desktop page still exists in listService.pageList
            if (listService.pageList.has(pageId)) {
                loadedPageIds.push(pageId);
                listService.addMobilePage(pageId, {
                    id: pageId,
                    name: pageData._name,
                    isNew: false
                });
            }
        });
        return loadedPageIds;
    }

    _includeNewFormPages(loadedPageIds) {

        const loadedPageIdSet = new Set(loadedPageIds);

        listService.pageList.forEach(desktopPageView => {
            const pageId = desktopPageView.id;

            if (!loadedPageIdSet.has(pageId)) {
                listService.addMobilePage(pageId, {
                    id: pageId,
                    name: desktopPageView.model.get('name'),
                    isNew: true,
                });

                const defaultColId = sequenceService.nextDomId();
                listService.addMobileColumn(defaultColId, {
                    id: defaultColId,
                    name: mobileConstants.CURRENT_COLUMN_NAME,
                    pageId: pageId,
                    order: mobileConstants.CURRENT_COLUMN_ORDER
                });
            }
        });
    }

    _loadFormColumns(mobileForm) {
        const columnsNode = mobileForm.Form?.Columns?.Column;
        const columns = ensureArray(columnsNode);

        if (!columns || columns.length === 0) {
            listService.resetMobileColumns();
            return;
        }

        listService.resetMobileColumns();

        columns.forEach(colData => {
            const pageId = listService.cleanId(colData?._pageId);
            if (colData && listService.mobilePages.has(pageId)) {
                listService.addMobileColumn(colData._id, {
                    id: colData._id,
                    name: colData._name,
                    pageId: pageId,
                    order: parseInt(colData._order, 10),
                    sections: Immutable.Map()
                });
            }
        });
    }

    _extractSectionProperties(mobileForm) {
        const sectionPropertiesMap = {};
        const savedObjectPages = ensureArray(mobileForm.Form?.Objects?.Object);

        savedObjectPages.forEach(pageGroup => {
            ensureArray(pageGroup.Objects?.Object).forEach(columnGroup => {
                ensureArray(columnGroup.Objects?.Object).forEach(sectionNode => {
                    if (sectionNode._type === 'section' && sectionNode.Properties) {
                        sectionPropertiesMap[listService.getCleanedId(sectionNode._id)] = sectionNode.Properties;
                    }
                });
            });
        });
        return sectionPropertiesMap;
    }

    _loadFormSections(mobileForm) {
        if (mobileForm.Form.Sections.Section) {
            const objects = Array.isArray(mobileForm.Form.Objects.Object) ? mobileForm.Form.Objects.Object : [mobileForm.Form.Objects.Object];

            let sectionProperties = [];
            objects.forEach(o => {
                let columns = Array.isArray(o.Objects.Object) ? o.Objects.Object : [o.Objects.Object];
                columns.forEach(c => {
                    if (c.Objects.Object) {
                        const sectionProps = Array.isArray(c.Objects.Object) ? c.Objects.Object : [c.Objects.Object];
                        let onlySections = sectionProps.filter(object => object._type === 'section').map(object => object);
                        sectionProperties = [...sectionProperties, ...onlySections];
                    }
                });
            });

            const sections = Array.isArray(mobileForm.Form.Sections.Section) ? mobileForm.Form.Sections.Section : [mobileForm.Form.Sections.Section];
            if (null === listService.mobileSections() || (listService.mobileSections() && listService.mobileSections().size === 0)) {
                sections.forEach(s => {
                    if (listService.pageList.get(listService.cleanId(s._pageId))) {
                        let section = {};
                        section.id = s._id;
                        section.name = s._name;
                        section.pageId = listService.cleanId(s._pageId);
                        section.columnId = s._columnId;
                        section.index = parseInt(s._index);
                        section.attributes = { "fontSize": utilMobile.GetDefaultFontByFactoryKey(mobileConstants.SECTION_FACTORY_KEY) };

                        const sectionProps = sectionProperties.find(props => props._id === s._id);

                        if (sectionProps && sectionProps.Properties) {
                            
                            section.attributes = normalizeSectionAttributesForWizard(sectionProps.Properties);

                            if (!section.attributes.titleText) {
                                section.attributes.titleText = section.name;
                            }
                        }
                        listService.addMobileSection(section.id, section);
                    }
                });
            }
        }
    }

    _loadFormObjects(mobileForm) {
        const objects = Array.isArray(mobileForm.Form.Objects.Object) ? mobileForm.Form.Objects.Object : [mobileForm.Form.Objects.Object];
        const objectsId = [];
        objects.forEach(o => {
            let columns = Array.isArray(o.Objects.Object) ? o.Objects.Object : [o.Objects.Object];
            columns.forEach(c => {
                if (parseInt(c._order) === -1) {//hidenObjects
                    if (c.Objects.Object) {
                        let hiddenControls = Array.isArray(c.Objects.Object) ? c.Objects.Object : [c.Objects.Object];
                        hiddenControls.forEach(ctl => {
                            let objectId = listService.cleanId(ctl._id);
                            let obj = listService.getObject(objectId);
                            if (obj) {
                                objectsId.push(objectId);
                                this._addObject(obj, true, false, '', ctl.Properties);
                            }
                        });
                    }
                }
                else {//Columns
                    if (c.Objects.Object) {
                        let sections = Array.isArray(c.Objects.Object) ? c.Objects.Object : [c.Objects.Object];
                        sections.forEach(s => {
                            if (s.Objects && s.Objects.Object) {
                                let controls = Array.isArray(s.Objects.Object) ? s.Objects.Object : [s.Objects.Object];

                                controls.forEach(ctl => {
                                    let objectId = listService.cleanId(ctl._id);
                                    let obj = listService.getObject(objectId);
                                    if (obj) {
                                        objectsId.push(objectId);
                                        this._addObject(obj, false, true, s._id, ctl.Properties);

                                        if (obj.model.attributes.factoryKey === 'control_group') {
                                            if (ctl.Objects.Object) {
                                                let childList = Array.isArray(ctl.Objects.Object) ? ctl.Objects.Object : [ctl.Objects.Object];
                                                let tabIndex = 0;
                                                childList.forEach(_innerCtl => {
                                                    let _objectId = listService.cleanId(_innerCtl._id);
                                                    let _obj = listService.getObject(_objectId);
                                                    if (_obj) {
                                                        tabIndex++;
                                                        objectsId.push(_objectId);
                                                        this._addObject(_obj, false, true, '', _innerCtl.Properties, { tabIndex: tabIndex });
                                                    }
                                                });
                                            }
                                        }
                                    }
                                });
                            }
                        });
                    }
                }
            });
        });
        return objectsId;
    }

    _buildColumnSectionLinks() {
        listService.mobilePages.forEach(page => {
            listService.addSectionsToColumn(page.id, mobileConstants.CURRENT_COLUMN_ORDER || 0);
        });
    }

    includeNewObjects(objectsId) {
        listService.objectList
            .filter(control => objectsId.indexOf(control.id) === -1 && control.id !== constants.FORM_RAW_ID)
            .forEach(control => {
                this._addObject(control, true, false, '');
            });
    }

    loadMobileFormDesign(mobileForm) {
        mobileListService.resetMobileObjMap();
        mobileListService.updateMobileObjMap(mobileForm);
        listService.updateDefaultColumnLayout();
    }

    addObject(desktopControlView, isHidden, savedProperties, pageId, sectionId, propertiesToMaintain) {

        const controlModel = desktopControlView.model;
        const factoryKey = controlModel.get('factoryKey');
        const controlId = controlModel.id;

        const baseControlData = processControlModel(controlModel);
        const mobileOverrideAttributes = convertSavedPropertiesToAttributes(savedProperties || {});

        const finalAttributes = {
            ...baseControlData.attributes,
            ...mobileOverrideAttributes
        };

        const object = {
            ...baseControlData,

            id: controlId,
            pageId: pageId,
            sectionId: sectionId,
            isHidden: isHidden,
            groupId: desktopControlView._groupId,
            showOnMobile: !isHidden,

            order: parseInt(savedProperties?._order || baseControlData.order || 0, 10),
            margins: this._parseMargins(savedProperties || finalAttributes),
            isNew: this._parseIsNew(savedProperties),

            height: ensurePxUnit(savedProperties?._height || baseControlData.height),
            width: ensurePxUnit(baseControlData.width),

            attributes: finalAttributes,

            mandatories: controlModel.get('mandatories'),
            state: controlModel.get('state'),
        };

        if (propertiesToMaintain && propertiesToMaintain.tabIndex) {
            object.tabIndex = propertiesToMaintain.tabIndex;
        }

        if (factoryKey === mobileConstants.HORIZONTAL_SEPARATOR_FACTORY_KEY) {
            object.height = ensurePxUnit(controlModel.get('height')); // Use desktop height
        }

        object.height = ensurePxUnit(object.height);
        object.width = ensurePxUnit(object.width);

        listService.addMobileObject(object.id, object);
    }

    _addObject(control, isHidden, showOnMobile, sectionId, attributes, propertiesToMaintain) {

        if (listService.pageList.get(control.pageId)) {

            const desktopModel = control.model.attributes;

            let object = {};
            object.id = desktopModel.id;
            object.name = desktopModel.name;
            object.isHidden = isHidden;
            object.groupId = control._groupId;
            object.pageId = control.pageId;
            if (propertiesToMaintain && propertiesToMaintain.tabIndex) {
                object.tabIndex = propertiesToMaintain.tabIndex;
            } else {
                object.tabIndex = desktopModel.tabIndex;
            }
            object.factoryKey = desktopModel.factoryKey;
            object.showOnMobile = showOnMobile;
            object.sectionId = sectionId;

            object.margins = this.getMargins(attributes);

            if (attributes && attributes._order) { object.order = attributes._order; }

            object.isNew = utilMobile.isControlNew(attributes);
            object.height = attributes && attributes._height ? attributes._height.replace('px', '') : utilMobile.getDefaultHeightByFactoryKey(object.factoryKey);

            if (object.factoryKey === mobileConstants.HORIZONTAL_SEPARATOR_FACTORY_KEY) {
                object.height = control.model.get('height');
            }

            object.mandatories = desktopModel.mandatories;
            object.state = desktopModel.state;
            
            const mobileSpecificProps = util._unPrefix(attributes);
            const fontInfo = this.getFontProperties(attributes, desktopModel.factoryKey);

            object.attributes = {
                ...mobileSpecificProps,
                ...fontInfo,
                "textAlign": attributes && attributes._align ? attributes._align : desktopModel.textAlignment,
                'marginSize': utilMobile.GetDefaultMarginByFactoryKey(mobileConstants.MARGIN_KEY).marginSize,
                'defaultMargin': utilMobile.GetDefaultMarginByFactoryKey(mobileConstants.MARGIN_KEY).defaultMargin
            };
            listService.addMobileObject(object.id, object);
        }
    }

    getFontProperties(attributes, factoryKey) {
        const defaultSize = utilMobile.GetDefaultFontByFactoryKey(factoryKey); 
        const defaultFamily = 'Poppins';
        const defaultWeight = 'normal';
        const defaultStyle = 'normal';
    
        let fontProps = {
            fontFamily: defaultFamily,
            fontSize: defaultSize,
            fontWeight: defaultWeight,
            fontStyle: defaultStyle
        };
    
        if (attributes && attributes._font) {
            const fonts = attributes._font.split(',');
            if (fonts[0]) fontProps.fontFamily = fonts[0];
            if (fonts[1]) fontProps.fontSize = fonts[1];
            if (fonts[2]) fontProps.fontWeight = fonts[2];
            if (fonts[3]) fontProps.fontStyle = fonts[3];
        }

        if (factoryKey === 'control_file_attachment' && attributes && attributes._buttonFont) {
            const btnFonts = attributes._buttonFont.split(',');
            
            fontProps.buttonFontFamily = btnFonts[0] || defaultFamily;
            fontProps.buttonFontSize = btnFonts[1] || defaultSize;
            fontProps.buttonTextWeight = btnFonts[2] || defaultWeight;
            fontProps.buttonTextStyle = btnFonts[3] || defaultStyle;
        }
    
        return fontProps;
    }

    getMargins(attributes) {
        const defaultMargins = { "top": 0, "right": 5, "bottom": 10, "left": 5 };
        if (attributes && attributes._margins) {
            let aux = attributes._margins.split(",");
            defaultMargins.top = aux[0];
            defaultMargins.right = aux[1];
            defaultMargins.bottom = aux[2];
            defaultMargins.left = aux[3];
        }

        return defaultMargins;
    }

    adjustControlMargins(control) {
        let margins = control.margins;
        margins.left = 5;
        margins.right = 5;
        if (control.order === 1 && margins.top === 0) {
            margins.top = 5;
        }
        if (control.order === listService.countMobileObjectsBySectionId(control.sectionId) && margins.bottom === 0) {
            margins.bottom = 5;
        }

        return margins;
    }

    _parseIsNew(savedProperties) { return savedProperties && (savedProperties._isNew === 'true' || savedProperties._isNew === true); }

    _parseMargins(propertiesOrAttributes) {
        const defaultMargins = { top: "0", right: "5", bottom: "10", left: "5" };
        let marginSource = propertiesOrAttributes?._margins;
        if (marginSource === undefined && propertiesOrAttributes?.margins) {
            const marginsObj = propertiesOrAttributes.margins;
            if (typeof marginsObj === 'object' && marginsObj !== null) {
                return {
                    top: String(marginsObj.top ?? defaultMargins.top), right: String(marginsObj.right ?? defaultMargins.right),
                    bottom: String(marginsObj.bottom ?? defaultMargins.bottom), left: String(marginsObj.left ?? defaultMargins.left),
                };
            }
            marginSource = propertiesOrAttributes.margins;
        }
        if (typeof marginSource === 'string') {
            const parts = marginSource.split(",");
            if (parts.length === 4) { return { top: parts[0].trim(), right: parts[1].trim(), bottom: parts[2].trim(), left: parts[3].trim() }; }
        }
        return defaultMargins;
    }
}

const mobileLoadService = new MobileLoadService();
export default mobileLoadService;